import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { formatDateTime } from '../format';
import { useDeleteMyNote, useMyNote, useMyNotes, useWipeMyNotes } from './hooks';

/** What Damis AI remembers about the signed-in user alone: read one note, delete one or all */
export function MiloMyNotes() {
  const { t, i18n } = useTranslation();
  const notes = useMyNotes(true);
  const remove = useDeleteMyNote();
  const wipe = useWipeMyNotes();
  const [reading, setReading] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [wiping, setWiping] = useState(false);
  const note = useMyNote(reading);

  return (
    <section className="space-y-3" data-qa-id="milo-my-notes">
      <div className="flex items-start gap-2">
        <h3 className="mr-auto text-sm font-semibold">{t('fleet.milo.myNotes.title')}</h3>
        {notes.data && notes.data.notes.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => setWiping(true)} data-qa-id="milo-my-notes-wipe">
            {t('fleet.milo.settings.notes.wipe')}
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{t('fleet.milo.myNotes.hint')}</p>
      {notes.isError && <p className="text-sm text-muted-foreground">{t('fleet.milo.myNotes.failed')}</p>}
      {notes.data &&
        (notes.data.notes.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('fleet.milo.myNotes.empty')}</p>
        ) : (
          <ul className="divide-y" data-qa-id="milo-my-notes-list">
            {notes.data.notes.map((item) => (
              <li key={item.path} className="flex items-center gap-1 py-2">
                <button type="button" className="min-w-0 flex-1 text-left" onClick={() => setReading(item.path)} data-qa-id="milo-my-note-open">
                  <span className="block truncate font-mono text-sm">{item.path}</span>
                  <span className="block text-xs text-muted-foreground">
                    {t('fleet.milo.settings.notes.chars', { count: item.chars })} · {t('fleet.milo.settings.notes.changed', { when: formatDateTime(item.updatedAt, i18n.language) })}
                  </span>
                </button>
                <Button variant="ghost" size="icon-sm" aria-label={t('fleet.milo.settings.notes.delete')} onClick={() => setDeleting(item.path)} data-qa-id="milo-my-note-delete">
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        ))}

      {reading !== null && (
        <Dialog open onOpenChange={(open) => !open && setReading(null)}>
          <DialogContent className="max-w-2xl" data-qa-id="milo-my-note-dialog">
            <DialogHeader>
              <DialogTitle className="truncate font-mono text-sm">{reading}</DialogTitle>
            </DialogHeader>
            <pre className="max-h-[60vh] overflow-auto rounded-md bg-muted p-3 text-xs whitespace-pre-wrap">{note.data?.content ?? '…'}</pre>
          </DialogContent>
        </Dialog>
      )}
      {deleting !== null && (
        <ConfirmDialog
          title={t('fleet.milo.settings.notes.deleteTitle', { path: deleting })}
          body={t('fleet.milo.settings.notes.deleteBody')}
          confirmLabel={t('fleet.milo.settings.notes.delete')}
          pending={remove.isPending}
          onConfirm={() => remove.mutate(deleting, { onSettled: () => setDeleting(null) })}
          onCancel={() => setDeleting(null)}
        />
      )}
      {wiping && (
        <ConfirmDialog
          title={t('fleet.milo.settings.notes.wipeTitle')}
          body={t('fleet.milo.myNotes.wipeBody')}
          confirmLabel={t('fleet.milo.settings.notes.wipe')}
          pending={wipe.isPending}
          onConfirm={() => wipe.mutate(undefined, { onSettled: () => setWiping(false) })}
          onCancel={() => setWiping(false)}
        />
      )}
    </section>
  );
}
